<script setup>
import { onMounted, watch, onUnmounted, inject, ref } from 'vue'

import { ViewController } from './controllers/view'

import { useRulerStore } from '@/store/ruler'
import { useMapStore } from '@/store/map'

import { getDataFromLS } from '@/utils'

import MapButton from '../map-button.vue'
import RulerConfirm from './ruler-confirm.vue'

const props = defineProps({
  mapgl: {
    type: Object,
    required: true
  }
})

const rulerStore = useRulerStore()
const mapStore = useMapStore()

const viewController = ref(null)
const $notify = inject('$notify')

watch(
  () => rulerStore.isShowNotify,
  (newValue) => {
    if (newValue) {
      const message = 'Значение длины скопировано в буфер обмена'
      $notify({
        message,
        type: 'success',
        duration: 3000
      })

      rulerStore.setShowNotify(false)
    }
  }
)

watch(
  () => rulerStore.clearAssets,
  (newValue) => {
    if (newValue) {
      viewController.value.clearMapAssets()
      rulerStore.setClearAssets(false)
      rulerStore.setTotalDistance(0)
    }
  }
)

watch(
  () => rulerStore.isRulerOn,
  (newValue) => {
    if (newValue) {
      if (props.mapgl.isStyleLoaded()) {
        viewController.value.addLines()
        viewController.value.addPoints()
      }
    } else {
      viewController.value?.clearMapAssets()
      mapStore.setActiveTool(null)
      rulerStore.setTotalDistance(0)
    }
  }
)

watch(
  () => rulerStore.points,
  (newValue, oldValue) => {
    if (newValue.length < 1) return
    if (newValue.length < oldValue.length) {
      viewController.value.updateLines()
      viewController.value.updatePoints()
      viewController.value.showPopup(rulerStore.lastPointData)
    }
  },
  { deep: true }
)

const toggleRuler = () => {
  if (!rulerStore.lastPointData && rulerStore.isRulerOn) {
    rulerStore.toggleRuler()
    return
  }

  if (mapStore.activeTool && mapStore.activeTool !== 'map-ruler') return

  if (!rulerStore.isRulerOn) {
    mapStore.setActiveTool('map-ruler')
    rulerStore.toggleRuler()
    return
  }

  const isShowRulerModal = getDataFromLS('show-ruler')

  if (isShowRulerModal !== false) {
    rulerStore.setIsShowRulerModal(true)
    return
  }

  const lastItem = rulerStore.points[rulerStore.points.length - 1]
  rulerStore.setLastPoint(lastItem)
  viewController.value.clearMapAssets()
  rulerStore.toggleRuler()
}

const turnOffRuler = () => {
  rulerStore.setRuler(false)
  rulerStore.setIsShowRulerModal(false)
  viewController.value.clearMapAssets()
  viewController.value.popupInstance?.unmount()
}

onMounted(() => {
  viewController.value = new ViewController(props.mapgl)
  viewController.value.addClickHandler()
})

onUnmounted(() => {
  turnOffRuler()
})
</script>

<template>
  <map-button
    v-tooltip="'Линейка'"
    icon="ruler"
    @click="toggleRuler"
    :active="rulerStore.isRulerOn"
    size="lg"
  />
  <ruler-confirm
    @closeModal="turnOffRuler"
    :isVisible="rulerStore.isShowRulerModal"
  />
</template>

<style lang="scss" scoped>
.map-ruler {
  gap: 0.5rem;
  background: var(--bg);
  border-radius: var(--border-radius);
  border: 1px solid var(--main-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--text);
  cursor: pointer;
  user-select: none;

  &.active {
    background: var(--primary);
  }
}
</style>
